# Route: /admin/roles/:roleName
# Role detail: role information + permissions

page-admin-role-detail:
  type: Page
  archetype: detail
  meta:
    title: $t.admin.roles.detail_title
    auth: required
  params:
    roleName:
      type: string
      required: true
  data:
    role:
      model: RoleDto
      key: $route.roleName
      refresh: on_mount
  spatial:
    display: flex
    direction: column
    gap: $spacing.lg
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
    responsive:
      sm:
        padding: $spacing.sm
        padding_inline: $spacing.sm
        gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - role-detail-header
    - role-detail-actions
    - role-detail-info
    - role-detail-permissions

# ── Header ───────────────────────────────────────────────────────────────────

role-detail-header:
  type: PageHeader
  back_route: /admin/roles
  title_prefix: $t.admin.roles.detail_title_prefix
  bind:
    content: role.name
  # F79 canonical /audit deep-link (screen-guide.md §6c) — new button per the
  # M1 manual test pass (Act 1.5): "in the detail of the role, I would
  # appreciate a button to navigate to the audit trail with the filters on
  # the entitytype and the current role". Roles have no separate id — the
  # role name IS both the identity and the audit EntityId
  # (RolePermissionsUpdated.RoleName, DefaultAuditEntryFactory).
  actions:
    - label: $t.admin.roles.btn_audit
      route: /audit
      query:
        entityType: Role
        entityId: $route.roleName
      return_context: origin_only
      variant: ghost

# ── Edit / Delete affordances ────────────────────────────────────────────────
# Each affordance is wrapped in a permission-gated Section (only NavItem /
# Section / SectionHeader nodes are permission-wrapped by the emitter). Edit
# gates on Role:update, Delete on Role:delete — matching the PUT/DELETE
# endpoints. Delete uses a type:custom DELETE so the emitted fetch surfaces the
# server's 409 "role in use" errors[] as a sticky toast, and only navigates
# back to the list on a 2xx.

role-detail-actions:
  type: Section
  spatial:
    display: flex
    gap: $spacing.md
    justify_content: flex-end
  children:
    - role-detail-edit-gate
    - role-detail-delete-gate

role-detail-edit-gate:
  type: Section
  permission: "Role:update"
  children:
    - role-detail-edit-btn

role-detail-edit-btn:
  type: Button
  color: info
  content: $t.admin.roles.btn_edit
  a11y:
    label: $t.admin.roles.btn_edit_label
  action:
    type: navigate
    route: /admin/roles/:roleName/edit
  spatial:
    min_height: $size.touch-target
    padding_inline: $spacing.lg
  tokens:
    radius: $radius.md

role-detail-delete-gate:
  type: Section
  permission: "Role:delete"
  children:
    - role-detail-delete-btn

role-detail-delete-btn:
  type: Button
  color: stop
  content: $t.admin.roles.btn_delete
  a11y:
    label: $t.admin.roles.btn_delete_label
  action:
    type: custom
    method: DELETE
    url: /api/users/roles/:roleName
    confirm: $t.admin.roles.delete_confirm
    on_success: navigate(/admin/roles)
  spatial:
    min_height: $size.touch-target
    padding_inline: $spacing.lg
  tokens:
    radius: $radius.md

# ── Info card ────────────────────────────────────────────────────────────────

role-detail-info:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
    padding: $spacing.lg
  tokens:
    background: $color_scheme.light.color.surface-elevated
    radius: $radius.lg
    shadow: $color_scheme.light.shadow.sm
  children:
    - role-info-section-title
    - role-info-name
    - role-info-description

role-info-section-title:
  type: Text
  content: $t.admin.roles.section_info
  a11y:
    role: heading
    level: 2
  tokens:
    font_size: $font.size.lg
    color: $color_scheme.light.color.on-surface

role-info-name:
  type: Section
  spatial:
    display: flex
    gap: $spacing.sm
  children:
    - role-info-name-label
    - role-info-name-value

role-info-name-label:
  type: Text
  content: $t.admin.roles.label_name
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

role-info-name-value:
  type: Text
  bind:
    content: role.name
  tokens:
    font_size: $font.size.sm

role-info-description:
  type: Section
  spatial:
    display: flex
    gap: $spacing.sm
  children:
    - role-info-desc-label
    - role-info-desc-value

role-info-desc-label:
  type: Text
  content: $t.admin.roles.label_description
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

role-info-desc-value:
  type: Text
  bind:
    content: role.description
  tokens:
    font_size: $font.size.sm

# ── Permissions ──────────────────────────────────────────────────────────────

role-detail-permissions:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
    padding: $spacing.lg
  tokens:
    background: $color_scheme.light.color.surface-elevated
    radius: $radius.lg
    shadow: $color_scheme.light.shadow.sm
  children:
    - role-perms-section-title
    - role-perms-matrix

role-perms-section-title:
  type: Text
  content: $t.admin.roles.section_permissions
  a11y:
    role: heading
    level: 2
  tokens:
    font_size: $font.size.lg
    color: $color_scheme.light.color.on-surface

# Read-only entity × action permission matrix — the same widget the create/edit
# screens use, embedded with `readonly` so cells are checked + disabled, with no
# row/column select-all toggles and no form-feed interceptor. The role name from
# the route prefills the matrix from the role's existing permissions[].
role-perms-matrix:
  type: Widget
  ref: xp87-role-permission-picker
  params:
    role-name: $params.roleName
    readonly: "true"
